import AppFormContainer from "@/components/AppFormContainer";
import ResultPanel from "@/components/comm-generator/ResultPanel";
import SideTitle from "@/components/SideTitle";
import SplitPane from "@/components/SplitPane";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Slider } from "@/components/ui/slider";
import SpeechTextArea from "@/components/ui/speech-text-area";
import { Eyeglasses } from "@phosphor-icons/react";
import { useState, useMemo, useEffect } from "react";
import { LanguageSelector } from "@/components/lesson-lab/LanguageSelector";
import { useTranslation } from "react-i18next";
import TutorialBox, { useTutorialState } from "@/components/TutorialBox";
import { GuidedTour, TourStep } from "@/components/GuidedTour";
import { useForm } from "react-hook-form";
import { useToolSubmission } from "@/lib/queries/tools/useToolSubmission"; // legacy (non-SSE)
import { useToolSubmissionSSE } from "@/lib/queries/tools/useToolSubmissionSSE";
import useSessionStorage from "@/lib/hooks/useSessionStorage";
import { DEFAULT_RESULT_LANGUAGE } from "@/lib/constants/languages";
import useUserSettings from "@/lib/queries/user/useUserSettings";
import { useStytchSession } from "@stytch/react";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";
import { resolveInitialToolLanguage } from "@/lib/utils/language";
import FloatingDocStatuses from "@/components/results/FloatingDocStatuses";
import { buildDocStatuses } from "@/components/results/buildDocStatuses";
import { useIsMobile } from "@/hooks/use-mobile";

type ResearcherForm = {
  subject: string;
  language: string;
  duration: number[]; // slider array
  type: string; // translation key
};

function Researcher() {
  const { t } = useTranslation(["researcher", "common"]);
  const isMobile = useIsMobile();
  const { session, fromCache } = useStytchSession();
  const { useGetUserSettings } = useUserSettings();
  const { data: userSettings } = useGetUserSettings({
    enabled: !!session && !fromCache,
  });
  const { language: a11yLanguage } = useA11ySettings();

  const initialLanguage = useMemo(
    () =>
      resolveInitialToolLanguage({
        a11yLanguage,
        userSettings,
        fallback: DEFAULT_RESULT_LANGUAGE,
      }),
    [a11yLanguage, userSettings]
  );

  const form = useForm<ResearcherForm>({
    defaultValues: {
      subject: "",
      language: initialLanguage,
      duration: [15],
      type: "type.researchNotes",
    },
  });

  const USE_SSE = true;
  const sse = USE_SSE ? useToolSubmissionSSE("researcher") : (null as any);
  const mutation = !USE_SSE ? useToolSubmission("researcher") : (null as any);
  const isPending = USE_SSE ? sse.isStreaming : Boolean(mutation?.isPending);
  const isSuccess = USE_SSE
    ? Boolean(sse.final || sse.latestOutput)
    : Boolean(mutation?.data);
  const data = USE_SSE ? sse.final : mutation?.data;
  const [storedIntermediate, setStoredIntermediate] = useState<string | null>(
    null
  );
  const [lastSubject, setLastSubject] = useState<string | null>(null);
  const [getPreferences, setPreferences] = useSessionStorage(
    "researcher-preferences",
    {}
  );

  // Sync if settings/a11y change and user hasn't modified language
  useEffect(() => {
    const desired = resolveInitialToolLanguage({
      a11yLanguage,
      userSettings,
      fallback: DEFAULT_RESULT_LANGUAGE,
    });
    const current = form.getValues("language");
    const dirty = (form.formState.dirtyFields as any)?.language;
    if (!dirty && current !== desired) {
      form.setValue("language", desired, { shouldDirty: false });
    }
  }, [a11yLanguage, userSettings, form, initialLanguage]);
  const [savePreferencesEnabled, setSavePreferencesEnabled] = useState(false);
  // Save preferences
  useEffect(() => {
    const sub = form.watch((value) => {
      if (!value) return;
      if (form.formState.isDirty && savePreferencesEnabled) {
        const filtered = {
          subject: value.subject,
          duration: value.duration,
          type: value.type,
          language: value.language,
        };
        setPreferences(filtered);
      }
    });
    return () => sub.unsubscribe();
  }, [form, savePreferencesEnabled, setPreferences]);

  // Load preferences
  useEffect(() => {
    if (getPreferences && Object.keys(getPreferences).length > 0) {
      form.reset({ ...form.getValues(), ...getPreferences });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tourOpen, setTourOpen] = useState(false);
  const tutorialState = useTutorialState("researcher-tutorial");
  const tourSteps: TourStep[] = useMemo(
    () => [
      {
        selector: "#side-title",
        title: t("tutorial.1.title"),
        content: t("tutorial.1.body", { ns: "researcher" }),
      },
      {
        selector: "#subject-form-item",
        title: t("tutorial.2.title"),
        content: t("tutorial.2.body", { ns: "researcher" }),
      },
      {
        selector: "#duration-form-item",
        title: t("tutorial.3.title"),
        content: t("tutorial.3.body", { ns: "researcher" }),
      },
      {
        selector: "#tour-language-container",
        title: t("tutorial.4.title"),
        content: t("tutorial.4.body", { ns: "researcher" }),
      },
      {
        selector: "#tour-generate",
        title: t("tutorial.5.title"),
        content: t("tutorial.5.body", { ns: "researcher" }),
      },
    ],
    [t]
  );

  const onSubmit = form.handleSubmit(
    ({ subject, language, duration, type }) => {
      const subjectChanged = lastSubject !== null && lastSubject !== subject;
      const intermediateValue = subjectChanged ? null : storedIntermediate;
      const payload = {
        subject,
        attachments: [],
        type: [
          {
            name: t(type, { lng: "en", ns: "researcher" }),
            recipient: "Researcher",
            duration: 0,
            durationUnit: undefined,
          },
        ],
        requirements: {
          age: null,
          language: language || DEFAULT_RESULT_LANGUAGE,
          learning_requirement: null,
        },
        regenerate: { intermediate: intermediateValue, feedback: null },
        timeout: duration[0],
      } as const;

      if (USE_SSE) {
        sse.start(payload as any);
      } else {
        (mutation as any).mutate(payload as any);
      }
      setLastSubject(subject);
    }
  );

  useEffect(() => {
    if (isSuccess && data?.intermediate) {
      setStoredIntermediate(data.intermediate);
    }
  }, [isSuccess, data?.intermediate]);

  return (
    <>
      <SplitPane
        showResultPanel={
          USE_SSE
            ? Boolean(sse.final || sse.latestOutput)
            : Boolean(mutation?.data)
        }
        key={isSuccess ? "research-result" : "research-form"}
      >
        <AppFormContainer
          form={form}
          onSubmit={onSubmit}
          isSuccess={isSuccess}
          isPending={isPending}
          savePreferences={{
            enabled: savePreferencesEnabled,
            onToggle: setSavePreferencesEnabled,
          }}
        >
          <SideTitle
            title={t("header.title")}
            intro={t("header.intro")}
            icon={Eyeglasses}
            showHelpIndicator={tutorialState.dismissed}
            onHelpClick={tutorialState.reopen}
          />

          <TutorialBox
            storageKey="researcher-tutorial"
            title={t("common:quickGuide", { defaultValue: "Quick guide" })}
            message={t("tutorial.blurb", {
              ns: "researcher",
            })}
            onStartTour={() => setTourOpen(true)}
            tutorialState={tutorialState}
          />
          <GuidedTour
            open={tourOpen}
            steps={tourSteps}
            storageKey="researcher-tutorial"
            onClose={() => setTourOpen(false)}
          />

          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem className="flex flex-col" id="subject-form-item">
                <FormLabel htmlFor="subject" className="mb-4 font-semibold">
                  {t("subject.label")}
                </FormLabel>
                <SpeechTextArea
                  {...field}
                  placeholder={[
                    t("subject.placeholder.1"),
                    t("subject.placeholder.2"),
                    t("subject.placeholder.3"),
                    t("subject.placeholder.4"),
                    t("subject.placeholder.5"),
                    t("subject.placeholder.6"),
                    t("subject.placeholder.7"),
                    t("subject.placeholder.8"),
                    t("subject.placeholder.9"),
                  ]}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="duration"
            render={({ field }) => (
              <FormItem className="flex flex-col" id="duration-form-item">
                <FormLabel>{t("duration.label")}</FormLabel>
                <FormDescription
                  id="duration-form-descriptor"
                  className="!text-light-grey mb-4"
                >
                  {t("duration.description")}
                </FormDescription>
                <Slider
                  defaultValue={[15]}
                  max={60}
                  min={5}
                  step={5}
                  value={field.value}
                  onValueChange={field.onChange}
                />
                <FormDescription
                  id="duration-form-descriptor"
                  className="mt-2 text-grey"
                >
                  {t("duration.feedback", { seconds: field.value[0] })}
                </FormDescription>
              </FormItem>
            )}
          />

          <LanguageSelector name="language" />
        </AppFormContainer>
        <ResultPanel
          result={
            USE_SSE
              ? data?.results
                ? data.results
                : sse.latestOutput
                  ? [sse.latestOutput]
                  : undefined
              : mutation?.data?.results
          }
          appResult="professor"
          isLoading={isPending}
          toolResponse={USE_SSE ? data : mutation?.data}
          sseEvents={USE_SSE ? sse.events : undefined}
          sseError={USE_SSE ? sse.error : (mutation?.error as any)}
          loadingStatuses={
            USE_SSE
              ? buildDocStatuses({
                  expectedDocs: [
                    {
                      type: t(form.getValues("type"), { ns: "researcher" }),
                      language:
                        (form.getValues("language") as string) ||
                        DEFAULT_RESULT_LANGUAGE,
                    },
                  ],
                  sseEvents: sse.events,
                  results:
                    (data?.results as any) ||
                    (sse.latestOutput ? [sse.latestOutput] : []),
                  isStreaming: sse.isStreaming,
                  isLoading: isPending,
                })
              : undefined
          }
          loadingStage={USE_SSE ? sse.stage : undefined}
          loadingProgress={USE_SSE ? sse.progress : undefined}
          onCancel={USE_SSE ? sse.abort : undefined}
        />
      </SplitPane>
      {USE_SSE &&
        (() => {
          const docStatuses = buildDocStatuses({
            expectedDocs: [
              {
                type: t(form.getValues("type"), { ns: "researcher" }),
                language:
                  (form.getValues("language") as string) ||
                  DEFAULT_RESULT_LANGUAGE,
              },
            ],
            sseEvents: sse.events,
            results:
              (data?.results as any) ||
              (sse.latestOutput ? [sse.latestOutput] : []),
            isStreaming: sse.isStreaming,
            isLoading: isPending,
          });
          const hasReady = docStatuses.some((s) => s.status === "ready");
          const showFloating =
            Boolean(sse.isStreaming) &&
            (isMobile || hasReady) &&
            docStatuses.length > 0 &&
            sse.stage !== "done" &&
            !sse.error;
          return (
            <FloatingDocStatuses
              visible={showFloating}
              statuses={docStatuses}
              stage={sse.stage}
              progress={sse.progress}
              onClose={() => {}}
              onCancel={sse.abort}
            />
          );
        })()}
    </>
  );
}

export default Researcher;
